import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOnboarding } from '../../context/OnboardingContext';
import {
  Sparkles,
  RefreshCw,
  Plus,
  Hand,
  Menu,
  CheckCircle,
  ChevronRight,
  ChevronLeft,
  X,
  ArrowDown,
} from 'lucide-react';

// Icon mapping
const iconMap = {
  sparkles: Sparkles,
  refresh: RefreshCw,
  plus: Plus,
  hand: Hand,
  menu: Menu,
  check: CheckCircle,
};

export default function OnboardingOverlay() {
  const {
    isActive,
    currentStep,
    currentStepIndex,
    totalSteps,
    isFirstStep,
    isLastStep,
    nextStep,
    prevStep,
    skipOnboarding,
  } = useOnboarding();

  const [targetRect, setTargetRect] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const overlayRef = useRef(null);

  // Find and track target element position
  useEffect(() => {
    if (!isActive || !currentStep?.target) {
      setTargetRect(null);
      return;
    }

    const updateTargetRect = () => {
      const element = document.querySelector(currentStep.target);
      if (element) {
        const rect = element.getBoundingClientRect();
        setTargetRect({
          top: rect.top,
          left: rect.left,
          width: rect.width,
          height: rect.height,
          bottom: rect.bottom,
          right: rect.right,
        });
      }
    };

    // Initial update
    updateTargetRect();

    // Update on scroll/resize
    window.addEventListener('scroll', updateTargetRect, true);
    window.addEventListener('resize', updateTargetRect);

    return () => {
      window.removeEventListener('scroll', updateTargetRect, true);
      window.removeEventListener('resize', updateTargetRect);
    };
  }, [isActive, currentStep]);

  // Animation on step change
  useEffect(() => {
    if (isActive) {
      setIsAnimating(true);
      const timer = setTimeout(() => setIsAnimating(false), 300);
      return () => clearTimeout(timer);
    }
  }, [currentStepIndex, isActive]);

  if (!isActive) return null;

  const Icon = iconMap[currentStep?.icon] || Sparkles;
  const isModalStep = currentStep?.type === 'modal';
  const isSpotlightStep = currentStep?.type === 'spotlight';

  // Calculate tooltip position
  const getTooltipStyle = () => {
    if (!targetRect || !isSpotlightStep) return {};

    const padding = 16;
    const arrowHeight = 12;

    if (currentStep.position === 'top') {
      // Show tooltip above the target
      return {
        bottom: `${window.innerHeight - targetRect.top + arrowHeight + padding}px`,
        left: '50%',
        transform: 'translateX(-50%)',
      };
    } else {
      // Show tooltip below the target
      return {
        top: `${targetRect.bottom + arrowHeight + padding}px`,
        left: '50%',
        transform: 'translateX(-50%)',
      };
    }
  };

  // Render spotlight overlay using box-shadow (works on all browsers including Safari)
  const renderSpotlightOverlay = () => {
    if (!targetRect || !isSpotlightStep) return null;

    const padding = 8;

    return (
      <div
        className="absolute pointer-events-none"
        style={{
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          borderRadius: 16,
          boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.75)',
          zIndex: 1,
        }}
      />
    );
  };

  // Render pulse animation around target
  const renderPulseRing = () => {
    if (!targetRect || !isSpotlightStep) return null;

    const padding = 8;

    return (
      <div
        className="absolute pointer-events-none"
        style={{
          top: targetRect.top - padding,
          left: targetRect.left - padding,
          width: targetRect.width + padding * 2,
          height: targetRect.height + padding * 2,
          zIndex: 2,
        }}
      >
        <div className="absolute inset-0 rounded-2xl border-2 border-white/50 animate-ping" />
        <div className="absolute inset-0 rounded-2xl border-2 border-white" />
      </div>
    );
  };

  const content = (
    <div
      ref={overlayRef}
      className="fixed inset-0 z-[10000]"
      onClick={(e) => {
        // Only allow click-through on spotlight steps if clicking the target
        if (isSpotlightStep && targetRect) {
          const { clientX, clientY } = e;
          const inTarget =
            clientX >= targetRect.left &&
            clientX <= targetRect.right &&
            clientY >= targetRect.top &&
            clientY <= targetRect.bottom;
          if (inTarget) {
            // Allow click through to target
            return;
          }
        }
        e.stopPropagation();
      }}
    >
      {/* Backdrop */}
      {isModalStep && (
        <div className="absolute inset-0 bg-black/75 backdrop-blur-sm" />
      )}

      {/* Spotlight overlay */}
      {isSpotlightStep && renderSpotlightOverlay()}

      {/* Pulse ring around target */}
      {isSpotlightStep && renderPulseRing()}

      {/* Modal Content (Welcome/Complete) */}
      {isModalStep && (
        <div className="absolute inset-0 flex items-center justify-center p-6">
          <div
            className={`bg-white rounded-3xl shadow-2xl max-w-sm w-full p-8 text-center transition-all duration-300 ${
              isAnimating ? 'scale-95 opacity-0' : 'scale-100 opacity-100'
            }`}
          >
            {/* Icon */}
            <div
              className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ backgroundColor: 'var(--accent-light, #e0f2fe)' }}
            >
              <Icon
                className="w-10 h-10"
                style={{ color: 'var(--accent-bg, #0ea5e9)' }}
              />
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-surface-900 mb-3">
              {currentStep.title}
            </h2>

            {/* Description */}
            <p className="text-surface-600 mb-8 leading-relaxed">
              {currentStep.description}
            </p>

            {/* Actions */}
            <div className="flex flex-col gap-3">
              {isFirstStep ? (
                <>
                  <button
                    onClick={nextStep}
                    className="w-full py-3 px-6 rounded-xl text-white font-semibold transition-all hover:opacity-90"
                    style={{ backgroundColor: 'var(--accent-bg, #0ea5e9)' }}
                  >
                    Let&apos;s Go!
                  </button>
                  <button
                    onClick={skipOnboarding}
                    className="text-surface-500 text-sm hover:text-surface-700 transition-colors"
                  >
                    Skip tutorial
                  </button>
                </>
              ) : (
                <button
                  onClick={nextStep}
                  className="w-full py-3 px-6 rounded-xl text-white font-semibold transition-all hover:opacity-90"
                  style={{ backgroundColor: 'var(--accent-bg, #0ea5e9)' }}
                >
                  Got it!
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Spotlight Tooltip */}
      {isSpotlightStep && (
        <div
          className={`absolute w-[calc(100%-32px)] max-w-sm transition-all duration-300 ${
            isAnimating ? 'opacity-0 translate-y-2' : 'opacity-100 translate-y-0'
          }`}
          style={{ ...getTooltipStyle(), zIndex: 10 }}
        >
          <div className="bg-white rounded-2xl shadow-2xl p-5 relative">
            {/* Arrow */}
            {currentStep.position === 'top' ? (
              <div
                className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45"
                style={{ boxShadow: '2px 2px 4px rgba(0,0,0,0.1)' }}
              />
            ) : (
              <div
                className="absolute -top-2 left-1/2 -translate-x-1/2 w-4 h-4 bg-white rotate-45"
                style={{ boxShadow: '-2px -2px 4px rgba(0,0,0,0.1)' }}
              />
            )}

            {/* Step indicator */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <div
                  className="w-8 h-8 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: 'var(--accent-light, #e0f2fe)' }}
                >
                  <Icon
                    className="w-4 h-4"
                    style={{ color: 'var(--accent-bg, #0ea5e9)' }}
                  />
                </div>
                <span className="text-xs font-medium text-surface-400">
                  {currentStepIndex + 1} of {totalSteps}
                </span>
              </div>
              <button
                onClick={skipOnboarding}
                className="p-1.5 rounded-lg hover:bg-surface-100 transition-colors"
              >
                <X className="w-4 h-4 text-surface-400" />
              </button>
            </div>

            {/* Content */}
            <h3 className="text-lg font-semibold text-surface-900 mb-2">
              {currentStep.title}
            </h3>
            <p className="text-sm text-surface-600 mb-4 leading-relaxed">
              {currentStep.description}
            </p>

            {/* Gesture hint for pull-refresh */}
            {currentStep.id === 'pull-refresh' && (
              <div className="flex items-center justify-center gap-2 py-2 mb-3 bg-surface-50 rounded-lg">
                <ArrowDown className="w-4 h-4 text-surface-500 animate-bounce" />
                <span className="text-xs text-surface-500">Pull down gesture</span>
              </div>
            )}

            {/* Gesture hint for swipe */}
            {currentStep.id === 'swipe-nav' && (
              <div className="flex items-center justify-center gap-2 py-2 mb-3 bg-surface-50 rounded-lg">
                <ChevronLeft className="w-4 h-4 text-surface-500" />
                <Hand className="w-4 h-4 text-surface-500" />
                <ChevronRight className="w-4 h-4 text-surface-500" />
                <span className="text-xs text-surface-500 ml-1">Swipe gesture</span>
              </div>
            )}

            {/* Navigation */}
            <div className="flex items-center justify-between">
              <button
                onClick={prevStep}
                disabled={isFirstStep}
                className={`flex items-center gap-1 text-sm font-medium transition-colors ${
                  isFirstStep
                    ? 'text-surface-300 cursor-not-allowed'
                    : 'text-surface-600 hover:text-surface-900'
                }`}
              >
                <ChevronLeft className="w-4 h-4" />
                Back
              </button>

              {/* Step dots */}
              <div className="flex items-center gap-1.5">
                {Array.from({ length: totalSteps }).map((_, i) => (
                  <div
                    key={i}
                    className={`w-2 h-2 rounded-full transition-all ${
                      i === currentStepIndex
                        ? 'w-4'
                        : ''
                    }`}
                    style={{
                      backgroundColor:
                        i === currentStepIndex
                          ? 'var(--accent-bg, #0ea5e9)'
                          : 'var(--surface-200, #e2e8f0)',
                    }}
                  />
                ))}
              </div>

              <button
                onClick={nextStep}
                className="flex items-center gap-1 text-sm font-semibold transition-colors"
                style={{ color: 'var(--accent-bg, #0ea5e9)' }}
              >
                {isLastStep ? 'Done' : 'Next'}
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  return createPortal(content, document.body);
}
